import { useMemo, useState } from "react";
import { ContentBlockEvents } from "../ContentBlockEvents";
import { PromptShape, PromptType, SelectionShape } from "@/models";
import { StyledUniversalSection } from "../Universal";
import { ChevronDownIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import { displayPromptType } from "@/util";

type DecisionLogProps = ContentBlockEvents & {
    contentBlockId: string;
    selections: (SelectionShape & { prompt: PromptShape })[];
    theme: any;
    userId: string;
    teamId: string;
};

const allowedPromptTypes = [
    PromptType["Numerical Input"],
    PromptType["Numerical Slider"],
    PromptType["Rank Order"],
    PromptType["Multiple Choice"],
    PromptType["Multiple Select"],
    PromptType["Long Text"],
    PromptType["Short Text"],
    PromptType["Drag and Drop"],
];

// Compact display components
function CompactTextDisplay({ selections }: { selections: SelectionShape[] }) {
    return (
        <div className="space-y-3">
            {selections.map((selection) => (
                <div
                    className="flex items-start space-x-4 rounded-lg border border-gray-200 bg-white p-4
                        shadow-sm"
                    key={selection.id}
                >
                    <div className="w-24 flex-shrink-0 text-xs font-medium uppercase tracking-wide text-gray-500">
                        {selection.user_name}
                    </div>
                    <div className="flex-1 text-sm leading-relaxed text-gray-900">
                        {selection.option_content}
                    </div>
                </div>
            ))}
        </div>
    );
}

function CompactMultiSelectDisplay({
    selections,
}: {
    selections: SelectionShape[];
}) {
    const selectionsByUser = useMemo(() => {
        return selections.reduce(
            (carry: { [index: string]: SelectionShape[] }, selection) => {
                if (!carry[selection.user_name])
                    carry[selection.user_name] = [];
                return {
                    ...carry,
                    [selection.user_name]: [
                        ...carry[selection.user_name],
                        selection,
                    ].sort((a, b) => a.numerical_value - b.numerical_value),
                };
            },
            {},
        );
    }, [selections]);

    return (
        <div className="space-y-4">
            {Object.keys(selectionsByUser).map((userName) => (
                <div
                    className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm"
                    key={userName}
                >
                    <div className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-700">
                        {userName}
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {selectionsByUser[userName].map((selection) => (
                            <span
                                className="inline-flex items-center rounded-full bg-blue-100 px-3 py-1.5 text-sm
                                    font-medium text-blue-800"
                                key={selection.id}
                            >
                                {selection.option_text}
                            </span>
                        ))}
                    </div>
                </div>
            ))}
        </div>
    );
}

function CompactMultipleChoiceDisplay({
    selections,
}: {
    selections: SelectionShape[];
}) {
    return (
        <div className="space-y-3">
            {selections.map((selection) => (
                <div
                    className="flex items-center space-x-4 rounded-lg border border-gray-200 bg-white p-4
                        shadow-sm"
                    key={selection.id}
                >
                    <div className="w-24 flex-shrink-0 text-sm font-medium text-gray-700">
                        {selection.user_name}
                    </div>
                    <div className="flex-1 text-sm font-semibold text-gray-900">
                        {selection.option_text}
                    </div>
                </div>
            ))}
        </div>
    );
}

function CompactNumericDisplay({
    selections,
}: {
    selections: SelectionShape[];
}) {
    const avgValue =
        selections.reduce((sum, s) => sum + s.numerical_value, 0) /
        selections.length;
    const minValue = Math.min(...selections.map((s) => s.numerical_value));
    const maxValue = Math.max(...selections.map((s) => s.numerical_value));

    return (
        <div className="space-y-4">
            {selections.length > 1 && (
                <div className="grid grid-cols-3 gap-3 rounded-lg border border-blue-200 bg-blue-50 p-4">
                    <div className="text-center">
                        <div className="text-lg font-bold text-blue-600">
                            {avgValue.toFixed(1)}
                        </div>
                        <div className="text-xs uppercase tracking-wide text-blue-500">
                            Average
                        </div>
                    </div>
                    <div className="text-center">
                        <div className="text-lg font-bold text-green-600">
                            {minValue}
                        </div>
                        <div className="text-xs uppercase tracking-wide text-green-500">
                            Min
                        </div>
                    </div>
                    <div className="text-center">
                        <div className="text-lg font-bold text-red-600">
                            {maxValue}
                        </div>
                        <div className="text-xs uppercase tracking-wide text-red-500">
                            Max
                        </div>
                    </div>
                </div>
            )}

            <div className="space-y-2">
                {selections.length > 1 && (
                    <div className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500">
                        Individual Responses
                    </div>
                )}
                <div className="space-y-2">
                    {selections.map((selection) => (
                        <div
                            className="flex items-center space-x-4 rounded-lg border border-gray-200 bg-white p-4
                                shadow-sm"
                            key={selection.id}
                        >
                            <div className="w-24 flex-shrink-0 text-sm font-medium text-gray-700">
                                {selection.user_name}
                            </div>
                            <div className="flex-1 text-right text-lg font-semibold text-gray-900">
                                {selection.numerical_value}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

function CompactRankOrderDisplay({
    selections,
}: {
    selections: SelectionShape[];
}) {
    const selectionsByUser = useMemo(() => {
        return selections.reduce(
            (carry: { [index: string]: SelectionShape[] }, selection) => {
                if (!carry[selection.user_name])
                    carry[selection.user_name] = [];
                return {
                    ...carry,
                    [selection.user_name]: [
                        ...carry[selection.user_name],
                        selection,
                    ].sort((a, b) => a.numerical_value - b.numerical_value),
                };
            },
            {},
        );
    }, [selections]);

    return (
        <div className="space-y-4">
            {Object.keys(selectionsByUser).map((userName) => (
                <div
                    className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm"
                    key={userName}
                >
                    <div className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-700">
                        {userName}
                    </div>
                    <div className="space-y-2">
                        {selectionsByUser[userName].map((selection, index) => (
                            <div
                                className="flex items-center gap-3 rounded-lg border border-gray-100 bg-gray-50 px-3 py-2"
                                key={selection.id}
                            >
                                <span
                                    className="flex h-6 w-6 items-center justify-center rounded-full bg-blue-500 text-xs
                                        font-bold text-white"
                                >
                                    {index + 1}
                                </span>
                                <span className="text-sm font-medium text-gray-900">
                                    {selection.option_text}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            ))}
        </div>
    );
}

// Collapsible Round Component
function CollapsibleRound({
    roundId,
    roundTitle,
    prompts,
    selectionsByPromptId,
}: {
    roundId: string;
    roundTitle: string;
    prompts: string[];
    selectionsByPromptId: {
        [index: string]: (SelectionShape & { prompt: PromptShape })[];
    };
}) {
    const [isExpanded, setIsExpanded] = useState(false);

    const getDisplayComponent = (
        promptType: string,
        selections: SelectionShape[],
    ) => {
        if (promptType.toLowerCase().includes("numerical")) {
            return <CompactNumericDisplay selections={selections} />;
        }
        if (promptType === PromptType["Rank Order"]) {
            return <CompactRankOrderDisplay selections={selections} />;
        }
        if (promptType === PromptType["Multiple Choice"]) {
            return <CompactMultipleChoiceDisplay selections={selections} />;
        }
        if (
            promptType === PromptType["Multiple Select"] ||
            promptType === PromptType["Drag and Drop"]
        ) {
            return <CompactMultiSelectDisplay selections={selections} />;
        }
        if (
            promptType === PromptType["Short Text"] ||
            promptType === PromptType["Long Text"]
        ) {
            return <CompactTextDisplay selections={selections} />;
        }
        return null;
    };

    return (
        <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
            <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="flex w-full items-center justify-between border-b border-gray-200 bg-gray-50
                    px-6 py-4 text-left transition-colors hover:bg-gray-100"
            >
                <div className="flex items-center gap-3">
                    <h2 className="text-lg font-bold text-gray-900">
                        {roundTitle}
                    </h2>
                    <span
                        className="inline-flex items-center rounded-full bg-gray-200 px-2.5 py-0.5 text-xs
                            font-medium text-gray-700"
                    >
                        {prompts.length} questions
                    </span>
                </div>
                {isExpanded ? (
                    <ChevronDownIcon className="h-5 w-5 text-gray-500" />
                ) : (
                    <ChevronRightIcon className="h-5 w-5 text-gray-500" />
                )}
            </button>

            {isExpanded && (
                <div className="space-y-4 p-6">
                    {prompts.map((promptId) => {
                        const promptType =
                            selectionsByPromptId[promptId][0].prompt_type;
                        const promptContent =
                            selectionsByPromptId[promptId][0]?.prompt?.content;

                        return (
                            <div
                                key={promptId}
                                className="rounded-lg border border-gray-200 bg-gray-50 p-5 shadow-sm"
                            >
                                <div className="mb-4">
                                    <h3 className="mb-2 line-clamp-2 text-base font-semibold text-gray-900">
                                        {promptContent}
                                    </h3>
                                    <div className="flex items-center gap-2 text-sm text-gray-500">
                                        <span
                                            className="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-xs
                                                font-medium text-blue-800"
                                        >
                                            {displayPromptType(promptType)}
                                        </span>
                                        <span>•</span>
                                        <span>
                                            {
                                                selectionsByPromptId[promptId]
                                                    .length
                                            }{" "}
                                            {selectionsByPromptId[promptId]
                                                .length === 1
                                                ? "response"
                                                : "responses"}
                                        </span>
                                    </div>
                                </div>

                                <div className="rounded-lg border border-gray-100">
                                    {getDisplayComponent(
                                        promptType,
                                        selectionsByPromptId[promptId],
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

export const DecisionLogTheme = ({
    contentBlockId,
    selections,
    theme,
    onClick,
    onHover,
    isInEditorContext,
}: DecisionLogProps) => {
    const selectionsByPromptId: {
        [index: string]: (SelectionShape & { prompt: PromptShape })[];
    } = useMemo(() => {
        // Mock selections for admin
        if (isInEditorContext) {
            return ["1"].reduce((carry, participantName) => {
                return {
                    ...carry,
                    [participantName]: [
                        {
                            id: participantName,
                            numerical_value: Math.random() * 100,
                            string_value: "",
                            is_selected: true,
                            prompt_text: "Lorem ipsum prompt text...",
                            option_text: "Option 1",
                            option_id: "1",
                            prompt_id: "1",
                            simulation_id: "1",
                            team_id: participantName,
                            user_id: participantName,
                            option_content: "Option 1",
                            // Joined columns
                            team_name: participantName,
                            user_name: participantName,
                            content_block_id: "1",
                            prompt_type: PromptType["Multiple Choice"],
                            scope: "Participant",
                            round_title: "Round 1",
                            round_id: "round_1",
                            prompt: {
                                content: `Lorem ipsum prompt text...${participantName}`,
                            },
                        },
                    ],
                };
            }, {});
        }

        return selections.reduce((carry, selection) => {
            if (!carry[selection.prompt_id]) {
                carry[selection.prompt_id] = [];
            }

            return {
                ...carry,
                [selection.prompt_id]: [
                    ...carry[selection.prompt_id],
                    selection,
                ],
            };
        }, {});
    }, [selections, contentBlockId]);

    const filteredPromptsKeys = useMemo(() => {
        return Object.keys(selectionsByPromptId)
            .filter((key) =>
                allowedPromptTypes.includes(
                    selectionsByPromptId[key][0].prompt_type,
                ),
            )
            .sort(
                (a, b) =>
                    selectionsByPromptId[a][0].prompt_weight -
                    selectionsByPromptId[b][0].prompt_weight,
            );
    }, [selectionsByPromptId]);

    const filteredPromptIdsGroupedByRoundId = useMemo(() => {
        return filteredPromptsKeys.reduce((carry, promptId) => {
            const roundId = selectionsByPromptId[promptId][0].round_id;

            if (!carry[roundId]) {
                carry[roundId] = [];
            }
            return {
                ...carry,
                [roundId]: [...carry[roundId], promptId],
            };
        }, {});
    }, [filteredPromptsKeys]);

    return (
        <StyledUniversalSection
            data-content-block-id={contentBlockId}
            position="relative"
            className="flex w-full flex-col space-y-3"
            {...theme}
        >
            {Object.keys(filteredPromptIdsGroupedByRoundId)?.length > 0 ? (
                Object.keys(filteredPromptIdsGroupedByRoundId)
                    .sort((a, b) => {
                        return (
                            selectionsByPromptId[
                                filteredPromptIdsGroupedByRoundId[a][0]
                            ][0].round_weight -
                            selectionsByPromptId[
                                filteredPromptIdsGroupedByRoundId[b][0]
                            ][0].round_weight
                        );
                    })
                    .map((roundId, idx) => (
                        <CollapsibleRound
                            key={roundId}
                            roundId={roundId}
                            roundTitle={
                                selectionsByPromptId[
                                    filteredPromptIdsGroupedByRoundId[
                                        roundId
                                    ][0]
                                ][0]?.round_title || `Round ${idx + 1}`
                            }
                            prompts={filteredPromptIdsGroupedByRoundId[roundId]}
                            selectionsByPromptId={selectionsByPromptId}
                        />
                    ))
            ) : (
                <div className="py-8 text-center">
                    <div className="mb-3 text-4xl text-gray-400">📊</div>
                    <div className="text-lg font-semibold text-gray-600">
                        No Decisions to Display
                    </div>
                    <div className="mt-1 text-sm text-gray-500">
                        Responses will appear here once participants make
                        selections
                    </div>
                </div>
            )}
        </StyledUniversalSection>
    );
};
